import { useEffect, useRef, useState, useMemo } from 'react';

/**
 * Custom hook to detect when an element is in viewport
 * @param {Object} options - IntersectionObserver options
 * @param {number} options.threshold - Percentage of element visible (0-1)
 * @param {string} options.rootMargin - Margin around viewport (e.g., "100px")
 * @returns {Array} [ref, inView] - Ref to attach to element and boolean if in view
 */
export const useInView = (options = {}) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  // Memoize observer options to prevent unnecessary re-renders
  const observerOptions = useMemo(() => ({
    threshold: options.threshold || 0.1,
    rootMargin: options.rootMargin || '50px',
    ...options
  }), [options]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          // Disconnect after first view to improve performance
          observer.disconnect();
        }
      },
      observerOptions
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
      observer.disconnect();
    };
  }, [observerOptions]);

  return [ref, inView];
};

export default useInView;
